import React, { useState, useEffect } from 'react';
import { CustomerIntegration, Integration, IntegrationWrapper } from './types';
import { refreshIntegrationStatus } from '../../api';

interface ActiveIntegrationsProps {
  integrations: IntegrationWrapper[];
  loading?: boolean;
  onRefresh?: (customerIntegrations: CustomerIntegration[]) => void;
}



const ActiveIntegrations: React.FC<ActiveIntegrationsProps> = ({ integrations, loading = false, onRefresh }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [previousRowCount, setPreviousRowCount] = useState(0);
  
  // Filter for configured integrations that have enabled customer integrations
  // const activeIntegrations = integrations.filter((integration: IntegrationWrapper) => 
  //   integration.customerIntegrations?.some((customerIntegration: CustomerIntegration) => customerIntegration.isEnabled)
  // );

  const enabledCustomerIntegrations = integrations.flatMap((integration: IntegrationWrapper) => 
    integration.customerIntegrations
      ?.filter((customerIntegration: CustomerIntegration) => customerIntegration.isEnabled)
      ?.map((customerIntegration: CustomerIntegration) => ({
        ...customerIntegration,
        integrationName: integration.integration.Name,
        categoryName: integration.integration.CategoryName
      })) || []
  );

  // Update previous row count when not refreshing
  React.useEffect(() => {
    if (!refreshing && enabledCustomerIntegrations.length > 0) {
      setPreviousRowCount(enabledCustomerIntegrations.length);
    }
  }, [enabledCustomerIntegrations.length, refreshing]);

  // Responsive: track mobile layout
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Helper function to format next run time
  const formatNextRun = (nextRun: string | null | undefined): string => {
    if (!nextRun) return 'Scheduling...';
    
    // Format the date as "March 10th, 21:37"
    try {
      const date = new Date(nextRun);
      return formatDateWithSuffix(date);
    } catch (e) {
      return nextRun;
    }
  };
  
  // Helper function to format last run time
  const formatLastRun = (lastRun: string | null | undefined): string => {
    if (!lastRun) return 'Never';
    
    // Format the date as "March 10th, 21:37"
    try {
      const date = new Date(lastRun);
      return formatDateWithSuffix(date);
    } catch (e) {
      return lastRun;
    }
  };
  
  // Helper function to format date with suffix (e.g., "March 10th, 21:37")
  const formatDateWithSuffix = (date: Date): string => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    
    // Add appropriate suffix to day
    let suffix = 'th';
    if (day % 10 === 1 && day !== 11) {
      suffix = 'st';
    } else if (day % 10 === 2 && day !== 12) {
      suffix = 'nd';
    } else if (day % 10 === 3 && day !== 13) {
      suffix = 'rd';
    }
    
    // Format time as HH:MM (24-hour format)
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${month} ${day}${suffix}, ${hours}:${minutes}`;
  };
  
  // Helper function to get status style based on status value
  const getStatusStyle = (status: string): React.CSSProperties => {
    // Default style for unknown status
    let backgroundColor = '#f5f5f5';
    let color = '#757575';
    
    // Map status values to appropriate colors
    switch (status?.toLowerCase()) {
      case 'running':
        backgroundColor = '#e3f2fd';
        color = '#1565c0';
        break;
      case 'completed':
        backgroundColor = '#e6f7e6';
        color = '#2e7d32';
        break;
      case 'failed':
        backgroundColor = '#ffebee';
        color = '#b71c1c';
        break;
      case 'pending':
        backgroundColor = '#fffde7';
        color = '#f9a825';
        break;
      default:
        // Keep default gray for unknown status
        break;
    }
    
    return { backgroundColor, color };
  };
  


  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setRefreshError(null);
      
      // Call the API to refresh integration status
      const results = await refreshIntegrationStatus();
      if (results.success) {
        // Then refresh the parent data to get the updated status
        if (onRefresh) {
          await onRefresh(results.customerIntegrations);
        }
        setLastRefreshed(new Date());
      }
    } catch (error) {
      console.error('Error refreshing integration status:', error);
      setRefreshError(error instanceof Error ? error.message : 'Failed to refresh status');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', rowGap: '8px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Active Integrations</h2>
        {isMobile && (
          <button
              onClick={handleRefresh}
              style={{
                padding: '8px 16px 8px 16px',
                backgroundColor: '#f0f0f0',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                width: '140px',
                textAlign: 'center',
                justifyContent: 'center'
              }}
              disabled={loading || refreshing}
            >
              {refreshing ? 'Refreshing...' : loading ? 'Loading...' : 'Refresh'}
            </button>
        )}
        {enabledCustomerIntegrations.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {lastRefreshed && (
              <span style={{ fontSize: '14px', color: '#757575' }}>
                Last refreshed: {formatDateWithSuffix(lastRefreshed)}
              </span>
            )}
            {!isMobile && 
              <button
                onClick={handleRefresh}
                style={{
                  padding: '8px 16px 8px 16px',
                  backgroundColor: '#f0f0f0',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  width: '140px',
                  textAlign: 'center',
                  justifyContent: 'center'
                }}
                disabled={loading || refreshing}
              >
                {refreshing ? 'Refreshing...' : loading ? 'Loading...' : 'Refresh'}
              </button>
            }
            </div>
        )}
      </div>

      {refreshError && (
        <div style={{ padding: '12px', backgroundColor: '#ffebee', color: '#b71c1c', borderRadius: '4px', marginBottom: '16px' }}>
          {refreshError}
        </div>
      )}

      {enabledCustomerIntegrations.length === 0 ? (
        <div style={{ marginTop: 16, marginBottom: 24 }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <thead>
              <tr style={{ 
                backgroundColor: '#f8fafc', 
                textAlign: 'left',
                borderBottom: '2px solid #e2e8f0'
              }}>
                <th style={{ 
                  padding: '16px 20px', 
                  minWidth: '150px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',                  
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Integration</th>
                <th style={{ 
                  padding: '16px 20px', 
                  minWidth: '120px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Category</th>
                <th style={{ 
                  padding: '16px 20px', 
                  minWidth: '160px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Last Run</th>
                <th style={{ 
                  padding: '16px 20px', 
                  minWidth: '160px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Next Run</th>
                <th style={{ 
                  padding: '16px 20px', 
                  minWidth: '200px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={5} style={{ 
                  padding: '48px 20px',
                  textAlign: 'center',
                  backgroundColor: '#f8fafc',
                  borderBottom: 'none'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '8px'
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                        <path d="M2 17l10 5 10-5"/>
                        <path d="M2 12l10 5 10-5"/>
                      </svg>
                    </div>
                    <h3 style={{ 
                      fontSize: '18px', 
                      fontWeight: '600',
                      color: '#374151', 
                      margin: 0 
                    }}>
                      No Active Integrations
                    </h3>
                    <p style={{ 
                      fontSize: '14px', 
                      color: '#6b7280', 
                      margin: 0,
                      maxWidth: '400px',
                      lineHeight: '1.5'
                    }}>
                      Enable an integration from the settings to see it here. Once enabled, you'll be able to monitor the status and data sync schedule.
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ marginTop: 16, marginBottom: 24 }}>
          <style>
            {`
              @keyframes pulse {
                0%, 100% {
                  opacity: 1;
                }
                50% {
                  opacity: 0.5;
                }
              }

              /* Responsive: show mobile list on small screens, table on larger */
              .ai-mobile-list { display: none; }
              .ai-desktop-table { display: block; }

              @media (max-width: 640px) {
                .ai-mobile-list { display: block; }
                .ai-desktop-table { display: none; }
              }
            `}
          </style>

          {/* Mobile stacked cards */}
          <div className="ai-mobile-list">
            {enabledCustomerIntegrations.map((customerIntegration) => (
              customerIntegration && (
                <div
                  key={`m-${customerIntegration.id}`}
                  style={{
                    background: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    padding: '14px 16px',
                    marginBottom: '12px',
                    border: '1px solid #f1f5f9'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{customerIntegration.integrationName}</div>
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        ...getStatusStyle(customerIntegration.status || '')
                      }}
                      title={customerIntegration.statusReason || ''}
                    >
                      {customerIntegration.status}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: '6px' }}>
                    <div style={{ color: '#6b7280', fontSize: '13px' }}>
                      <span style={{ color: '#374151', fontWeight: 500 }}>Category: </span>
                      {customerIntegration.categoryName}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '13px' }}>
                      <span style={{ color: '#374151', fontWeight: 500 }}>Last run: </span>
                      {formatLastRun(customerIntegration.lastRunTime)}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '13px' }}>
                      <span style={{ color: '#374151', fontWeight: 500 }}>Next run: </span>
                      {formatNextRun(customerIntegration.nextScheduledRun)}
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>

          {/* Desktop table */}
          <table className="ai-desktop-table" style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <thead>
              <tr style={{ 
                backgroundColor: '#f8fafc', 
                textAlign: 'left',
                borderBottom: '2px solid #e2e8f0'
              }}>
                <th style={{ 
                  padding: '16px 20px', 
                  minWidth: '150px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Integration</th>
                <th style={{ 
                  padding: '16px 20px', 
                  minWidth: '120px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Category</th>
                <th style={{ 
                  padding: '16px 20px', 
                  minWidth: '160px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Last Run</th>
                <th style={{ 
                  padding: '16px 20px', 
                  minWidth: '160px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Next Run</th>
                <th style={{ 
                  padding: '16px 20px', 
                  minWidth: '200px',
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {refreshing ? (
                // Show placeholder rows during refresh to maintain height
                Array.from({ length: previousRowCount || 1 }).map((_, index) => (
                  <tr key={`placeholder-${index}`} style={{ 
                    borderBottom: '1px solid #f1f5f9', 
                    height: '30px',
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc'
                  }}>
                    <td style={{ padding: '16px 20px', minWidth: '150px', verticalAlign: 'middle', width: '100%' }}>
                      <div style={{ 
                        height: '27px', 
                        backgroundColor: '#e2e8f0', 
                        borderRadius: '6px',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }} />
                    </td>
                    <td style={{ padding: '16px 20px', minWidth: '120px', verticalAlign: 'middle' }}>
                      <div style={{ 
                        height: '27px', 
                        backgroundColor: '#e2e8f0', 
                        borderRadius: '6px',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }} />
                    </td>
                    <td style={{ padding: '16px 20px', minWidth: '160px', verticalAlign: 'middle' }}>
                      <div style={{ 
                        height: '27px', 
                        backgroundColor: '#e2e8f0', 
                        borderRadius: '6px',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }} />
                    </td>
                    <td style={{ padding: '16px 20px', minWidth: '160px', verticalAlign: 'middle' }}>
                      <div style={{ 
                        height: '27px', 
                        backgroundColor: '#e2e8f0', 
                        borderRadius: '6px',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }} />
                    </td>
                    <td style={{ padding: '16px 20px', minWidth: '200px', verticalAlign: 'middle' }}>
                      <div style={{ 
                        height: '27px', 
                        backgroundColor: '#e2e8f0', 
                        borderRadius: '6px',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }} />
                    </td>
                  </tr>
                ))
              ) : (
                enabledCustomerIntegrations.map((customerIntegration) => (
                  customerIntegration && (
                    <tr key={customerIntegration.id} style={{ 
                      borderBottom: '1px solid #f1f5f9', 
                      height: '60px',
                      backgroundColor: enabledCustomerIntegrations.indexOf(customerIntegration) % 2 === 0 ? '#ffffff' : '#f8fafc',
                      transition: 'background-color 0.2s ease'
                    }}>
                    <td style={{ 
                      padding: '16px 20px', 
                      minWidth: '150px', 
                      verticalAlign: 'middle',
                      fontWeight: '500',
                      color: '#1f2937',
                      width: '100%'
                    }}>{customerIntegration.integrationName}</td>
                    <td style={{ 
                      padding: '16px 20px', 
                      minWidth: '120px', 
                      verticalAlign: 'middle',
                      color: '#6b7280'
                    }}>{customerIntegration.categoryName}</td>                  
                    <td style={{ 
                      padding: '16px 20px', 
                      minWidth: '160px', 
                      verticalAlign: 'middle',
                      color: '#6b7280',
                      fontSize: '14px'
                    }}>{formatLastRun(customerIntegration.lastRunTime)}</td>
                    <td style={{ 
                      padding: '16px 20px', 
                      minWidth: '160px', 
                      verticalAlign: 'middle',
                      color: '#6b7280',
                      fontSize: '14px'
                    }}>{formatNextRun(customerIntegration.nextScheduledRun)}</td>                  
                    <td style={{ padding: '16px 20px', minWidth: '200px', verticalAlign: 'middle' }}>
                      <span 
                        style={{ 
                          padding: '6px 12px', 
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          cursor: customerIntegration.statusReason ? 'help' : 'default',
                          ...getStatusStyle(customerIntegration.status || '')
                        }}
                        title={customerIntegration.statusReason || ''}
                      >
                        {customerIntegration.status}
                      </span>
                    </td>
                    </tr>
                  )
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActiveIntegrations;   
